/**
 * The summary card (design §5). Since 2026-08-27 it holds TWO rows: the AI
 * Generated Summary on top (passed in via `aiSummary` — it used to be its own
 * card directly above this one), then the 3 tiles — Rental Range Eligibility /
 * Move-In Date / Savings & Investments. Every box in both rows now carries the
 * shared DETAIL_CARD chrome, so the nested cards and their parent read as one
 * design language. AI-derived values come from the opaque
 * aiReportData payload via display.ts (data-map §4 — no DTO widening);
 * the move-in date comes from the typed property step. Gaps render '-'.
 */
import {
	Calendar,
	CalendarDays,
	CircleDollarSign,
	HandCoins,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { deriveSummaryTileValues } from '#app/utils/application-detail-v2/display'
import { resolveAiSummaryText } from '#app/utils/application-detail-v2/report-display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { cn } from '#app/utils/misc'
import { DETAIL_CARD, IconSquare, type PastelTone } from './primitives'

function Tile({
	tone,
	icon,
	label,
	value,
	badge,
	muted = false,
	className,
}: {
	tone: PastelTone
	icon: ReactNode
	label: string
	value: string
	badge?: ReactNode
	/** Placeholder value (no data yet) — grey, not the value ink (client
	 * 2026-09-07: "Waiting on applicant" reads in #747474). */
	muted?: boolean
	className?: string
}) {
	return (
		<div
			className={cn(
				// Same DETAIL_CARD chrome as the AI Generated Summary row above and
				// the card wrapping both (client 2026-08-27) — the bordered
				// rounded-2xl tile it replaced read as a different design language.
				DETAIL_CARD,
				'relative flex flex-col items-center gap-3 px-6 py-6 text-center',
				className,
			)}
		>
			{badge}
			<IconSquare tone={tone} className="size-11 rounded-xl">
				{icon}
			</IconSquare>
			<span className="text-sm text-[#747474]">{label}</span>
			<span
				className={cn(
					'-mt-2 text-[24px] font-semibold',
					muted ? 'text-[#747474]' : 'text-[#32302F]',
				)}
			>
				{value}
			</span>
		</div>
	)
}

/** Presentational row shared with the combined page (typed values in). */
export function SummaryTilesView({
	rentRange,
	savings,
	propertyStep,
	isGuarantor = false,
	aiSummary,
	hasAiSummary = Boolean(aiSummary),
	heading,
	pendingLabel = '-',
}: {
	rentRange: string | null
	savings: string | null
	propertyStep: ParsedStepData['property']
	/** Card title above the rows — the in-progress state's "Application in
	 * progress" (client 2026-09-07). Absent on the regular report render. */
	heading?: string
	/** What a missing AI value reads as. The report render keeps '-'; the
	 * in-progress state says "Waiting on applicant". */
	pendingLabel?: string
	/** First row of this card: the AI Generated Summary body. Self-hiding — it
	 * renders null (and no spacing) when the payload carries no narrative, so
	 * the tiles sit alone exactly as before. */
	aiSummary?: ReactNode
	/** The slot can be a self-hiding element that still renders null. Side-by-side
	 * only when that narrative is actually present, so a lone savings tile sits
	 * on the left instead of the right column. */
	hasAiSummary?: boolean
	/** A guarantor isn't renting/moving in — hide the Rental Range Eligibility
	 * and Move-In Date tiles for them (matches the PDF). Savings still applies. */
	isGuarantor?: boolean
}) {
	const { formatShortDate } = useFormatDate()
	const moveInDate = propertyStep?.moveInDate
	const flexible = propertyStep?.moveInDateFlexible === 'yes'

	const savingsTile = (
		<Tile
			tone="purple"
			icon={<HandCoins />}
			label="Savings & Investments"
			value={savings ?? pendingLabel}
			muted={savings == null}
			className={
				isGuarantor && hasAiSummary ? 'h-full justify-center' : undefined
			}
		/>
	)

	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			{heading ? (
				<h2 className="mb-5 text-[16px] font-semibold text-[#32302F]">
					{heading}
				</h2>
			) : null}
			{isGuarantor && hasAiSummary ? (
				<div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(240px,320px)]">
					<div className="min-w-0 [&>div]:mb-0 [&>div]:flex [&>div]:h-full [&>div]:flex-col">
						{aiSummary}
					</div>
					{savingsTile}
				</div>
			) : (
				<>
					{aiSummary}
					<div
						className={cn(
							'grid grid-cols-1 gap-6',
							isGuarantor
								? // A guarantor shows only the Savings & Investments tile —
									// align it to the start rather than stretching full width.
									'max-w-sm'
								: 'sm:grid-cols-2 lg:grid-cols-3',
						)}
					>
						{!isGuarantor ? (
							<>
								<Tile
									tone="green"
									icon={<CircleDollarSign />}
									label="Rental Range Eligibility"
									value={rentRange ?? pendingLabel}
									muted={rentRange == null}
								/>
								<Tile
									tone="rose"
									icon={<CalendarDays />}
									label="Move-In Date"
									value={moveInDate ? formatShortDate(moveInDate) : '-'}
									badge={
										// v1 parity (RentalPreferencesSection): flag flexible move-ins.
										flexible ? (
											<span className="absolute top-3 right-4 rounded-full bg-[#32302F] px-2.5 py-0.5 text-xs text-white">
												Flexible
											</span>
										) : undefined
									}
								/>
							</>
						) : null}
						{savingsTile}
					</div>
				</>
			)}
		</section>
	)
}

export function SummaryTiles({
	aiReportData,
	propertyStep,
	applicationType = 'primary',
	aiSummary,
	aiGeneratedPilot = false,
}: {
	aiReportData: Record<string, unknown> | null
	propertyStep: ParsedStepData['property']
	applicationType?: 'primary' | 'co_applicant' | 'guarantor'
	/** Passed straight through — see SummaryTilesView. */
	aiSummary?: ReactNode
	/** Same pilot flag the AI card uses, so an empty narrative does not keep
	 * the guarantor savings tile in the right column. */
	aiGeneratedPilot?: boolean
}) {
	const { rentRange, savings } = deriveSummaryTileValues(aiReportData)
	const hasAiSummary = Boolean(
		resolveAiSummaryText({ aiReportData, aiGeneratedPilot }),
	)
	return (
		<SummaryTilesView
			rentRange={rentRange}
			savings={savings}
			propertyStep={propertyStep}
			isGuarantor={applicationType === 'guarantor'}
			aiSummary={aiSummary}
			hasAiSummary={hasAiSummary}
		/>
	)
}
